import axios from "axios";
import type { FilterOptions, SearchFilters, SearchResponse, LoginRequest, LoginResponse, AdminDocument, AdminUpdateRequest, SearchFacets, RateLimitExceeded, RateLimitConfig, SearchResultLimitConfig } from "../types";
import { getToken, isSessionExpired, redirectToLogin } from "./auth";

const API_BASE_URL = "";

/**
 * Rejected requests carry this when the admin session has lapsed. A redirect to
 * the login page is already under way, so callers should let it pass rather than
 * rendering an error the admin will never have time to read.
 */
export class SessionExpiredError extends Error {
  constructor() {
    super("SESSION_EXPIRED");
    this.name = "SessionExpiredError";
  }
}

/**
 * Thrown when the backend returns HTTP 429 (global hourly AI Search limit hit).
 * Carries the rateLimit payload so the UI can show the retry time / countdown.
 */
export class RateLimitError extends Error {
  info: RateLimitExceeded;
  constructor(info: RateLimitExceeded) {
    super("RATE_LIMIT_EXCEEDED");
    this.name = "RateLimitError";
    this.info = info;
  }
}

/**
 * If the error is an axios 429 with the documented envelope, rethrow it as a
 * typed RateLimitError; otherwise rethrow the original error unchanged.
 */
function rethrowRateLimit(err: unknown): never {
  if (axios.isAxiosError(err) && err.response?.status === 429) {
    const data = err.response.data as { rateLimit?: RateLimitExceeded } | undefined;
    if (data?.rateLimit) {
      throw new RateLimitError(data.rateLimit);
    }
  }
  throw err;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Endpoints that sit behind `get_current_admin_user` on the backend.
 *
 * Only these may trigger the expired-session redirect. Document search and
 * download are public: a stale token left in localStorage must not bounce an
 * ordinary visitor to the admin login page.
 */
const PROTECTED_PATHS = ["/api/admin", "/api/confidence", "/api/documents/upload"];

function requiresAuth(url: string | undefined): boolean {
  if (!url) return false;
  return PROTECTED_PATHS.some((path) => url.startsWith(path));
}

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = getToken();
  if (!token) {
    return config;
  }

  if (isSessionExpired(token)) {
    // Don't spend a round-trip proving what the token already tells us: on a
    // protected call, bail out to login now rather than sending a request that
    // can only come back 401. On a public call, just drop the dead header and
    // let the request through.
    if (requiresAuth(config.url)) {
      redirectToLogin({ expired: true });
      return Promise.reject(new SessionExpiredError());
    }
    return config;
  }

  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Backstop for what the local expiry check cannot see: a token revoked early, a
// backend SECRET_KEY rotation, or clock skew that made a dead token look live.
// A 401 from a protected endpoint means the session is over, whatever `exp` said.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      axios.isAxiosError(error) &&
      error.response?.status === 401 &&
      getToken() &&
      requiresAuth(error.config?.url)
    ) {
      redirectToLogin({ expired: true });
      return Promise.reject(new SessionExpiredError());
    }
    return Promise.reject(error);
  },
);

export const searchAPI = {
  // Search documents with facets
  searchDocuments: async (filters: SearchFilters, includeFacets: boolean = true): Promise<SearchResponse> => {
    try {
      const response = await api.post("/api/search/", filters, {
        params: { include_facets: includeFacets }
      });
      return response.data;
    } catch (err) {
      rethrowRateLimit(err); // -> RateLimitError on HTTP 429
    }
  },

  // Get facets only (for cascading filters)
  getFacets: async (filters: SearchFilters): Promise<SearchFacets> => {
    try {
      const response = await api.post("/api/search/facets", filters);
      return response.data.facets;
    } catch (err) {
      rethrowRateLimit(err); // -> RateLimitError on HTTP 429
    }
  },

  // Get filter options for document types
  getDocumentTypes: async (): Promise<string[]> => {
    const response = await api.get("/api/search/filters/document-types");
    return response.data.document_types || [];
  },

  // Get filter options for outside agencies
  getOutsideAgencies: async (): Promise<string[]> => {
    const response = await api.get("/api/search/filters/outside-agencies");
    return response.data.outside_agencies || [];
  },

  // Get all filter options at once
  getAllFilterOptions: async (): Promise<FilterOptions> => {
    const [document_types, outside_agencies] = await Promise.all([
      searchAPI.getDocumentTypes(),
      searchAPI.getOutsideAgencies(),
    ]);

    return {
      document_types,
      outside_agencies,
    };
  },
};

export const documentAPI = {
  // Download document
  downloadDocument: async (documentId: string): Promise<void> => {
    const response = await api.get(`/api/documents/${documentId}/download`, {
      responseType: "blob",
      // Large PDFs stream from Azure Blob Storage; allow a generous window so a
      // slow (but working) transfer isn't aborted, while still failing loudly
      // instead of hanging forever with no feedback.
      timeout: 120000,
    });

    // Guard against an "empty success" — a 200 with a zero-byte body would
    // otherwise save a broken file silently.
    const blob = new Blob([response.data]);
    if (blob.size === 0) {
      throw new Error("Downloaded file is empty");
    }

    // Create blob link to download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    // Get filename from content-disposition header.
    // Use [^"]+ so the trailing quote isn't captured into the filename.
    const contentDisposition = response.headers["content-disposition"];
    let filename = "document.pdf";
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();

    // Defer cleanup: revoking the object URL synchronously right after click()
    // can cancel the download before the browser dispatches it, which is the
    // likely cause of "sometimes the document doesn't download at all".
    setTimeout(() => {
      link.remove();
      window.URL.revokeObjectURL(url);
    }, 1000);
  },

  // Get document metadata
  getDocumentMetadata: async (documentId: string) => {
    const response = await api.get(`/api/documents/${documentId}/metadata`);
    return response.data;
  },

  // Get document URL
  getDocumentUrl: async (documentId: string): Promise<string> => {
    const response = await api.get(`/api/documents/${documentId}/url`);
    return response.data.url;
  },
  
  // Upload document (admin only)
  uploadDocument: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append("file", file);
    
    const response = await api.post("/api/documents/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },
};

export const adminAPI = {
  // Login - redirects to Azure AD OAuth
  login: () => {
    window.location.href = "/api/admin/login";
  },
  
  // Get current user info
  getCurrentUser: async () => {
    const response = await api.get("/api/admin/me");
    return response.data;
  },
  
  // Get all documents for review
  getDocumentsForReview: async (skip = 0, top = 50, requires_review_only = false): Promise<{ documents: AdminDocument[], count: number }> => {
    const response = await api.get("/api/admin/documents", {
      params: { skip, top, requires_review_only }
    });
    return response.data;
  },
  
  // Get document details
  getDocumentDetails: async (documentId: string): Promise<AdminDocument> => {
    const response = await api.get(`/api/admin/documents/${documentId}`);
    return response.data.document;
  },
  
  // Update document metadata
  updateDocumentMetadata: async (documentId: string, updateData: AdminUpdateRequest): Promise<any> => {
    const response = await api.put(`/api/admin/documents/${documentId}`, updateData);
    return response.data;
  },
  
  // Approve document for public visibility
  approveDocument: async (documentId: string): Promise<any> => {
    const response = await api.put(`/api/admin/documents/${documentId}/approve`);
    return response.data;
  },

  // Delete document
  deleteDocument: async (documentId: string): Promise<any> => {
    const response = await api.delete(`/api/admin/documents/${documentId}`);
    return response.data;
  },

  // Get the current global AI Search rate-limit configuration + live usage
  getRateLimitConfig: async (): Promise<RateLimitConfig> => {
    const response = await api.get("/api/admin/rate-limit-config");
    return response.data;
  },

  // Update the global hourly AI Search limit (takes effect on the next search)
  updateRateLimitConfig: async (maxRequests: number): Promise<RateLimitConfig> => {
    const response = await api.put("/api/admin/rate-limit-config", {
      max_requests: maxRequests,
    });
    return response.data;
  },

  // Get the fixed cap on results returned by relevancy-ranked public search
  getSearchResultLimitConfig: async (): Promise<SearchResultLimitConfig> => {
    const response = await api.get("/api/admin/search-result-limit-config");
    return response.data;
  },

  updateSearchResultLimitConfig: async (maxResults: number): Promise<SearchResultLimitConfig> => {
    const response = await api.put("/api/admin/search-result-limit-config", {
      max_results: maxResults,
    });
    return response.data;
  },

  // Get the current extraction-confidence threshold (fraction 0.0–1.0).
  // Documents below this confidence are flagged for admin review on upload.
  // Returns null when no value has been configured yet.
  getConfidenceScore: async (): Promise<number | null> => {
    const response = await api.get("/api/confidence/get-score");
    return response.data?.confidence_score ?? null;
  },

  // Update the extraction-confidence threshold (fraction 0.0–1.0).
  updateConfidenceScore: async (confidenceScore: number): Promise<number> => {
    const response = await api.post("/api/confidence/update-score", {
      confidence_score: confidenceScore,
    });
    return response.data.confidence_score;
  },
};

export default api;
